import { useRef } from "react"
import { DEFAULT_STATE, errorStyles, isSectionComplete } from "../Utilities/constants"
import { CrashDataField, SectionProps } from "../Types/calculator"
import { Card, AccordionCollapse, FormGroup, FormLabel, FormControl, FormCheck } from "react-bootstrap"
import { SectionIndicators } from "./Common"
import { incrementDecrementArrows } from "../Utilities/interface"
import { getNumberOfCrashes, getVariable } from "../Utilities/calculations"

export function CrashData(props: SectionProps<CrashDataField>) {
  const { state, section, setSettings, onCategorySelected } = props
  const { expandedSections, numFatalityCrashes, numInjuryCrashes, numPropertyDamageCrashes, useDefaultCrashData } = state
  const previousCrashData = useRef({
    numFatalityCrashes: 0,
    numInjuryCrashes: 0,
    numPropertyDamageCrashes: 0,
  })

  const complete = isSectionComplete(state, section)
  const validatePropertyDamageCrashes = section.fields["numPropertyDamageCrashes"].validationFunction
  const validateInjuryCrashes = section.fields["numInjuryCrashes"].validationFunction
  const validateFatalityCrashes = section.fields["numFatalityCrashes"].validationFunction

  const { numberOfFatalityCrashes, numberOfInjuryCrashes, numberOfPDOCrashes } = getNumberOfCrashes(state)
  
  const handleCheck = (ev: any) => {
    if (ev.target.checked) {
      previousCrashData.current = {
        numPropertyDamageCrashes: state.numPropertyDamageCrashes,
        numInjuryCrashes: state.numInjuryCrashes,
        numFatalityCrashes: state.numFatalityCrashes,
      }
    }
    setSettings({
      useDefaultCrashData: ev.target.checked ? "Yes" : "No",
      ...(ev.target.checked
        ? {
            numPropertyDamageCrashes: DEFAULT_STATE.numPropertyDamageCrashes,
            numInjuryCrashes: DEFAULT_STATE.numInjuryCrashes,
            numFatalityCrashes: DEFAULT_STATE.numFatalityCrashes,
          }
        : { ...previousCrashData.current }),
    })
  }

  return (
    <Card id={section.name}>
      <Card.Header
        tabIndex={70}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCategorySelected(section.name)}
        as="h3"
        onClick={() => onCategorySelected(section.name)}
      >
        <div style={{ fontWeight: complete ? "lighter" : "inherit" }}>
          {section.name}
        </div>
        <SectionIndicators
          error={!complete && expandedSections.includes(section.name)}
          complete={complete}
          tooltipMessage="Please fill in the number of crashes your fleet had last year for each level of severity. The calculator will combine this crash data with the mileage data above and average costs based on crash severity to calculate the potential benefits of the technologies. The calculator can also use industry averages per 100 million miles based on the mileage you input above."
        />
      </Card.Header>
      <AccordionCollapse className="IncludeInExport" eventKey={section.name}>
        <Card.Body>
          <FormGroup style={{ marginBottom: "0.75rem" }}>
            <FormLabel aria-label="Property Damage Only Crashes">
              What is the total number of Property Damage Only crashes you had last year?
              <FormControl
                tabIndex={72}
                value={numberOfPDOCrashes}
                min={0}
                type="number"
                disabled={useDefaultCrashData === "Yes"}
                onChange={(ev) => setSettings({ numPropertyDamageCrashes: Number(ev.target.value) })}
                onBlur={(ev) => setSettings({ numPropertyDamageCrashes: Number(ev.target.value) })}
                onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "numPropertyDamageCrashes", state, setSettings, onlyUpDown: true })}
                style={!validatePropertyDamageCrashes(numPropertyDamageCrashes) ? errorStyles : undefined}
              />
              <span style={{ fontSize: "0.75rem" }}>Industry average is {getVariable("Default PDO Rate")} per 100 million miles</span>
            </FormLabel>
          </FormGroup>
          <FormGroup style={{ marginBottom: "0.75rem" }}>
            <FormLabel aria-label="Injury Crashes">
              What is the total number of Injury crashes you had last year?
              <FormControl
                tabIndex={73}
                value={numberOfInjuryCrashes}
                min={0}
                disabled={useDefaultCrashData === "Yes"}
                onChange={(ev) => setSettings({ numInjuryCrashes: Number(ev.target.value) })}
                onBlur={(ev) => setSettings({ numInjuryCrashes: Number(ev.target.value) })}
                onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "numInjuryCrashes", state, setSettings, onlyUpDown: true })}
                style={!validateInjuryCrashes(numInjuryCrashes) ? errorStyles : undefined}
              />
              <span style={{ fontSize: "0.75rem" }}>Industry average is {getVariable("Default Injury Rate")} per 100 million miles</span>
            </FormLabel>
          </FormGroup>
          <FormGroup style={{ marginBottom: "0.75rem" }}>
            <FormLabel aria-label="Fatality Crashes">
              What is the total number of Fatality crashes you had last year?
              <FormControl
                tabIndex={74}
                value={numberOfFatalityCrashes}
                min={0}
                disabled={useDefaultCrashData === "Yes"}
                onChange={(ev) => setSettings({ numFatalityCrashes: Number(ev.target.value) })}
                onBlur={(ev) => setSettings({ numFatalityCrashes: Number(ev.target.value) })}
                onKeyDown={(ev) => incrementDecrementArrows({ ev, field: "numFatalityCrashes", state, setSettings, onlyUpDown: true })}
                style={!validateFatalityCrashes(numFatalityCrashes) ? errorStyles : undefined}
              />
              <span style={{ fontSize: "0.75rem" }}>Industry average is {getVariable("Default Fatality Rate")} per 100 million miles</span>
            </FormLabel>
          </FormGroup>
          <FormGroup>
            <FormCheck
              tabIndex={75}
              inline
              label="Use industry averages based on your mileage"
              checked={useDefaultCrashData === "Yes"}
              onChange={handleCheck}
            />
          </FormGroup>
        </Card.Body>
      </AccordionCollapse>
    </Card>
  )
}